import React from 'react';
import { Box, Tooltip } from '@mui/joy';

interface StackSegment {
    label: string;
    value: number;
    color: string;
}

interface StackedLinearProgressComponentProps {
    segments: StackSegment[];
    height?: number | string;
    width?: number | string;
    showTooltip?: boolean;
}

function StackedLinearProgressComponent({
    segments,
    height = '12px',
    width = '100%',
    showTooltip = true
}: StackedLinearProgressComponentProps) {
    // Calculate total to get percentages
    const total = segments.reduce((sum, segment) => sum + segment.value, 0);

    // Map segments to percentages
    const percentages = segments.map(segment => ({
        ...segment,
        percentage: total > 0 ? (segment.value / total) * 100 : 0
    }));

    const stackedBar = (
        <Box
            sx={{
                display: 'flex',
                width: width,
                height: height,
                borderRadius: '4px',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0'
            }}>
            {percentages.map((segment, index) => (
                <Box
                    key={`${segment.label}-${index}`}
                    sx={{
                        flex: segment.percentage,
                        backgroundColor: segment.color,
                        transition: 'flex 0.3s ease',
                        '&:not(:last-child)': {
                            borderRight: '2px solid white'
                        }
                    }}
                />
            ))}
        </Box>
    );

    if (!showTooltip) {
        return stackedBar;
    }

    const tooltipTitle = (
        <div>
            {percentages.map((segment, index) => (
                <div key={`${segment.label}-${index}`} style={{ marginBottom: '4px' }}>
                    <strong>{segment.label}:</strong> {Math.round(segment.percentage)}%
                </div>
            ))}
        </div>
    );

    return (
        <Tooltip
            title={tooltipTitle}
            variant="solid"
            placement="top"
            sx={{
                fontSize: '0.75rem',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                backgroundColor: '#333',
                color: '#fff',
                maxWidth: '200px',
                textAlign: 'center'
            }}>
            {stackedBar}
        </Tooltip>
    );
}

export default StackedLinearProgressComponent;
